import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button, Table, useTable, Input, Select } from "@repo/o37-ui";
import { ArrowLeft, Plus, Trash2, Link as LinkIcon, Search } from "lucide-react";
import { HoldingsVendorMapping, HoldingsSubscription } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function VendorMappings() {
  const navigate = useNavigate();
  const [mappings, setMappings] = useState<HoldingsVendorMapping[]>([]);
  const [subscriptions, setSubscriptions] = useState<HoldingsSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });

  // New mapping form
  const [showForm, setShowForm] = useState(false);
  const [rawName, setRawName] = useState('');
  const [normalizedName, setNormalizedName] = useState('');
  const [linkedSubscriptionId, setLinkedSubscriptionId] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [saving, setSaving] = useState(false);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [mappingsRes, subsRes] = await Promise.all([
        fetch(`${API_URL}/holdings/vendor-mappings`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/subscriptions`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        })
      ]);

      if (mappingsRes.ok) {
        const data = await mappingsRes.json();
        setMappings(data.map((m: any) => ({
          id: m.id,
          rawName: m.raw_name,
          normalizedName: m.normalized_name,
          subscriptionId: m.subscription_id,
          isRecurring: m.is_recurring,
          createdAt: m.created_at
        })));
      }

      if (subsRes.ok) {
        const data = await subsRes.json();
        setSubscriptions(data.map((s: any) => ({
          id: s.id,
          vendorName: s.vendor_name
        })));
      }
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!rawName.trim() || !normalizedName.trim()) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/holdings/vendor-mappings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({
          rawName: rawName.trim(),
          normalizedName: normalizedName.trim(),
          subscriptionId: linkedSubscriptionId || null,
          isRecurring
        })
      });

      if (response.ok) {
        setShowForm(false);
        setRawName('');
        setNormalizedName('');
        setLinkedSubscriptionId('');
        setIsRecurring(false);
        loadData();
      }
    } catch (e) {
      console.error('Failed to create mapping:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this vendor mapping?')) return;

    try {
      const response = await fetch(`${API_URL}/holdings/vendor-mappings/${id}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });

      if (response.ok) {
        loadData();
      }
    } catch (e) {
      console.error('Failed to delete mapping:', e);
    }
  };

  const columns = useMemo(() => [
    {
      id: "rawName",
      header: "Raw Name (from bank)",
      cell: ({ row }: { row: { original: HoldingsVendorMapping } }) => (
        <span className="font-mono text-sm text-neutral-900">
          {row.original.rawName}
        </span>
      )
    },
    {
      id: "normalizedName",
      header: "Normalized Name",
      cell: ({ row }: { row: { original: HoldingsVendorMapping } }) => (
        <span className="font-medium text-neutral-900">
          {row.original.normalizedName}
        </span>
      )
    },
    {
      id: "linkedSubscription",
      header: "Linked Subscription",
      cell: ({ row }: { row: { original: HoldingsVendorMapping } }) => {
        const sub = subscriptions.find(s => s.id === row.original.subscriptionId);
        if (sub) {
          return (
            <div className="flex items-center gap-1.5 text-blue-600">
              <LinkIcon className="w-3.5 h-3.5" />
              <span>{sub.vendorName}</span>
            </div>
          );
        }
        return <span className="text-neutral-400">-</span>;
      }
    },
    {
      id: "recurring",
      header: "Recurring",
      cell: ({ row }: { row: { original: HoldingsVendorMapping } }) => (
        row.original.isRecurring ? (
          <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
            Yes
          </span>
        ) : (
          <span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-500 rounded-full">
            No
          </span>
        )
      )
    },
    {
      id: "actions",
      header: "",
      size: 80,
      cell: ({ row }: { row: { original: HoldingsVendorMapping } }) => (
        <Button
          variant="outline"
          className="h-8 text-xs px-2 text-red-600 border-red-200 hover:bg-red-50"
          onClick={() => handleDelete(row.original.id)}
          icon={<Trash2 className="w-3.5 h-3.5" />}
          text=""
        />
      )
    }
  ], [subscriptions]);

  const { table, ...tableProps } = useTable({
    data: mappings,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: mappings.length,
    resourceName: (plural) => `mapping${plural ? "s" : ""}`,
  });

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Vendor Mappings</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/holdings/bank-imports')}
          className="flex items-center gap-1.5 text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Bank Imports</span>
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold mb-1">Vendor Mappings</h1>
            <p className="text-neutral-500">
              Map bank transaction descriptions to normalized vendor names
            </p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            icon={<Plus className="w-4 h-4" />}
            text="Add Mapping"
          />
        </div>
      </div>

      {/* Add Mapping Form */}
      {showForm && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="font-semibold text-neutral-900 mb-4">New Vendor Mapping</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Raw Name (from bank) *
                </label>
                <Input
                  value={rawName}
                  onChange={(e) => setRawName(e.target.value)}
                  placeholder="e.g., AMZN MKTP US*123ABC"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Normalized Name *
                </label>
                <Input
                  value={normalizedName}
                  onChange={(e) => setNormalizedName(e.target.value)}
                  placeholder="e.g., Amazon"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Link to Subscription
                </label>
                <Select
                  value={linkedSubscriptionId}
                  onChange={(e) => setLinkedSubscriptionId(e.target.value)}
                >
                  <option value="">None</option>
                  {subscriptions.map(s => (
                    <option key={s.id} value={s.id}>{s.vendorName}</option>
                  ))}
                </Select>
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    className="rounded border-neutral-300"
                  />
                  <span className="font-medium text-neutral-700">Recurring expense</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-neutral-100">
              <Button
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setRawName('');
                  setNormalizedName('');
                  setLinkedSubscriptionId('');
                  setIsRecurring(false);
                }}
                text="Cancel"
              />
              <Button
                onClick={handleCreate}
                disabled={saving || !rawName.trim() || !normalizedName.trim()}
                text={saving ? 'Saving...' : 'Add Mapping'}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mappings Table */}
      <Card>
        <CardContent className="p-0">
          {mappings.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <Search className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <h3 className="text-lg font-medium mb-2">No Vendor Mappings</h3>
              <p className="mb-6">
                Add mappings to automatically match bank transactions.
              </p>
              <Button
                onClick={() => setShowForm(true)}
                icon={<Plus className="w-4 h-4" />}
                text="Add Mapping"
              />
            </div>
          ) : (
            <Table {...tableProps} table={table} />
          )}
        </CardContent>
      </Card>

      {/* Help Text */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <h3 className="font-semibold text-neutral-900 mb-3">How Vendor Mappings Work</h3>
          <div className="text-sm text-neutral-600 space-y-2">
            <p>
              When you import bank statements, transaction descriptions often contain cryptic codes
              (e.g., "AMZN MKTP US*123ABC"). Vendor mappings help normalize these to recognizable names.
            </p>
            <p>
              You can also link mappings directly to subscriptions, allowing automatic matching of
              future transactions to track subscription costs.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
